import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/class-name";

const alertVariants = cva(
    "relative grid w-full items-start gap-x-2 gap-y-0.5 rounded-xl border px-3.5 py-3 text-sm text-card-foreground has-data-[slot=alert-action]:grid-cols-[1fr_auto] has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-2 has-[>svg]:has-data-[slot=alert-action]:grid-cols-[calc(var(--spacing)*4)_1fr_auto] [&>svg]:h-[1lh] [&>svg]:w-4",
    {
        variants: {
            variant: {
                default: "bg-transparent dark:bg-input/32 [&>svg]:text-muted-foreground",
                info: "border-info/32 bg-info/4 [&>svg]:text-info",
                success: "border-success/32 bg-success/4 [&>svg]:text-success",
                warning: "border-warning/32 bg-warning/4 [&>svg]:text-warning",
                error: "border-destructive/32 bg-destructive/4 [&>svg]:text-destructive",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    },
);

function Alert({ className, variant, ...props }: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
    return <div data-slot={"alert"} role={"alert"} className={cn(alertVariants({ variant }), className)} {...props} />;
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
    return <div data-slot={"alert-title"} className={cn("font-medium [svg~&]:col-start-2", className)} {...props} />;
}

function AlertDescription({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot={"alert-description"}
            className={cn("flex flex-col gap-2.5 text-muted-foreground [svg~&]:col-start-2", className)}
            {...props}
        />
    );
}

function AlertAction({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot={"alert-action"}
            className={cn(
                "flex gap-1 max-sm:col-start-2 max-sm:mt-2 sm:row-start-1 sm:row-end-3 sm:self-center sm:[[data-slot=alert-description]~&]:col-start-2 sm:[[data-slot=alert-title]~&]:col-start-2 sm:[svg~&]:col-start-2 sm:[svg~[data-slot=alert-description]~&]:col-start-3 sm:[svg~[data-slot=alert-title]~&]:col-start-3",
                className,
            )}
            {...props}
        />
    );
}

export { Alert, AlertTitle, AlertDescription, AlertAction };
